<template>
  <div class="otp-verification-container">
    <div class="otp-card">
      <div class="text-center mb-4">
        <img :src="brandLogo" :alt="brandName + ' Logo'" class="logo mb-3" />
        <h2>Enter your OTP</h2>
        <p class="text-muted">
          We have sent a 6 digit OTP to your email at
          <span class="font-weight-bold">{{ maskedEmail }}</span>
        </p>
      </div>

      <form @submit.prevent="verifyOtpHandler">
        <div class="otp-inputs d-flex justify-content-center mb-4">
          <input
            v-for="(digit, index) in otpDigits"
            :key="index"
            v-model="otpDigits[index]"
            :ref="(el) => (otpInputs[index] = el)"
            type="text"
            maxlength="1"
            class="otp-input text-center form-control mx-1"
            @input="(event) => handleInput(event, index)"
            @keydown="(event) => handleKeyDown(event, index)"
            @paste="index === 0 ? handlePaste($event) : null"
            :disabled="isVerifyingOtp"
          />
        </div>

        <div class="text-center mb-4">
          <p class="text-muted">
            Didn't receive an OTP?
            <a
              href="#"
              @click.prevent="resendOtpHandler"
              :class="{ 'disabled-link': isResendingOtp || resendCooldown > 0 }"
            >
              Resend
              <span v-if="resendCooldown > 0"> ({{ resendCooldown }}s)</span>
            </a>
          </p>
        </div>

        <div class="d-grid">
          <button
            type="submit"
            class="btn btn-primary btn-block"
            :disabled="!isOtpComplete || isVerifyingOtp"
          >
            <span
              v-if="isVerifyingOtp"
              class="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
            {{ isVerifyingOtp ? "Verifying..." : "Verify OTP" }}
          </button>
        </div>
      </form>
      <div class="footer-text text-center mt-5">
        <p>
          &copy; PT Anugrah Multi Remittance {{ new Date().getFullYear() }} |
          <a href="#">Term & Conditions</a> | <a href="#">Privacy Policy</a>
        </p>
      </div>
    </div>
    <div class="footer-text-bottom text-center mt-3">
      <p>
        &copy; PT Anugrah Multi Remittance {{ new Date().getFullYear() }} |
        <a href="#">Term & Conditions</a> | <a href="#">Privacy Policy</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { sendOtp, verifyOtp } from "@/api/transfer";
import { useBranding } from "@/utils/branding";

const router = useRouter();
const store = useStore();

// Get branding information
const { brandLogo, brandName } = useBranding();

store.state.hideConfigButton = false;
store.state.showTransferNavbar = true;
store.state.showNavbar = false;
store.state.showSidenav = false;
store.state.showFooter = false;

const otpDigits = ref(["", "", "", "", "", ""]);
const otpInputs = ref([]);
const maskedEmail = ref("");
const batchUUID = ref("");
const paymentMethod = ref("");
const bankId = ref("");

const isVerifyingOtp = ref(false);
const isResendingOtp = ref(false);
const resendCooldown = ref(0);
let cooldownTimer = null;

const isOtpComplete = computed(() =>
  otpDigits.value.every((digit) => digit !== "" && /^[0-9]$/.test(digit))
);
const combinedOtp = computed(() => otpDigits.value.join(""));

const populateFromState = () => {
  const state = history.state;
  if (state) {
    maskedEmail.value = state.maskedEmail || "";
    batchUUID.value = state.batchUUID || "";
    paymentMethod.value = state.paymentMethod || "";
    bankId.value = state.bankId || "";
  }

  // If data is crucial and might not be in state (e.g., refresh), redirect or show error
  if (!batchUUID.value) {
    console.warn(
      "OtpVerification: BatchUUID not found in history.state. This can happen on page refresh."
    );
    // Optionally redirect to a previous step or show an error message
    // router.push({ name: 'Transfer_baru' }); // Example redirect
    // store.dispatch('showToast', { type: 'error', message: 'Session data lost. Please restart the payment process.' });
  }

  otpDigits.value = ["", "", "", "", "", ""];
  if (otpInputs.value[0]) {
    otpInputs.value[0].focus();
  }
};

onMounted(() => {
  populateFromState();
  if (otpInputs.value[0]) {
    otpInputs.value[0].focus();
  }
});

const handleInput = (event, index) => {
  const value = event.target.value;
  if (value.length === 1 && /^[0-9]$/.test(value)) {
    otpDigits.value[index] = value;
    if (index < otpDigits.value.length - 1) {
      otpInputs.value[index + 1].focus();
    }
  } else if (value.length === 0) {
    // Allow backspace to clear
    otpDigits.value[index] = "";
  } else {
    // If not a digit or more than one char, revert to previous value
    event.target.value = otpDigits.value[index];
  }
};

const handleKeyDown = (event, index) => {
  if (event.key === "Backspace") {
    if (otpDigits.value[index] === "" && index > 0) {
      otpInputs.value[index - 1].focus();
    }
    otpDigits.value[index] = ""; // Ensure current is cleared before moving
  } else if (event.key === "ArrowLeft" && index > 0) {
    otpInputs.value[index - 1].focus();
  } else if (event.key === "ArrowRight" && index < otpDigits.value.length - 1) {
    otpInputs.value[index + 1].focus();
  }
};

const handlePaste = (event) => {
  event.preventDefault();
  const pastedText = (event.clipboardData || window.clipboardData)
    .getData("text")
    .trim();
  const numbers = pastedText.replace(/\D/g, "");
  const sixDigits = numbers.slice(0, 6);

  if (sixDigits.length > 0) {
    for (let i = 0; i < sixDigits.length && i < otpDigits.value.length; i++) {
      otpDigits.value[i] = sixDigits[i];
    }
    // Focus the next empty input or the last filled input
    const nextFocusIndex =
      sixDigits.length < otpDigits.value.length
        ? sixDigits.length
        : otpDigits.value.length - 1;
    if (otpInputs.value[nextFocusIndex]) {
      otpInputs.value[nextFocusIndex].focus();
    }
    // If all digits are filled by paste, attempt to verify
    if (isOtpComplete.value) {
      verifyOtpHandler();
    }
  }
};

const startResendCooldown = () => {
  resendCooldown.value = 60; // 60 seconds cooldown
  cooldownTimer = setInterval(() => {
    resendCooldown.value -= 1;
    if (resendCooldown.value <= 0) {
      clearInterval(cooldownTimer);
      cooldownTimer = null;
    }
  }, 1000);
};

onUnmounted(() => {
  if (cooldownTimer) {
    clearInterval(cooldownTimer);
  }
});

const resendOtpHandler = async () => {
  if (isResendingOtp.value || resendCooldown.value > 0) return;

  // Ensure batchUUID is available, potentially re-fetch from state if necessary
  // or handle if it's missing (e.g. after a refresh where state is lost)
  if (!batchUUID.value) {
    const currentState = history.state;
    if (currentState && currentState.batchUUID) {
      batchUUID.value = currentState.batchUUID;
      paymentMethod.value = currentState.paymentMethod;
      bankId.value = currentState.bankId;
      maskedEmail.value = currentState.maskedEmail; // also restore masked email
    } else {
      store.dispatch("showToast", {
        type: "error",
        message: "Cannot resend OTP. Session data missing.",
      });
      isResendingOtp.value = false;
      return;
    }
  }

  isResendingOtp.value = true;
  try {
    const formData = new FormData();
    formData.append("batch_id", batchUUID.value);
    // Use the paymentMethod and bankId retrieved from state
    if (paymentMethod.value) formData.append("method", paymentMethod.value);
    if (bankId.value) formData.append("bank_id", bankId.value);

    const response = await sendOtp(formData);
    store.dispatch("showToast", {
      type: "success",
      message: "OTP has been resent to your email.",
    });
    // Update maskedEmail from response, or ensure it was correctly passed in state
    maskedEmail.value =
      response.data.masked_email ||
      (history.state && history.state.maskedEmail) ||
      "";
    startResendCooldown();
  } catch (error) {
    console.error("Error resending OTP:", error);
    const errorMessage =
      error.response?.data?.message ||
      "Failed to resend OTP. Please try again.";
    store.dispatch("showToast", { type: "error", message: errorMessage });
  }
  isResendingOtp.value = false;
};

const verifyOtpHandler = async () => {
  if (!isOtpComplete.value || isVerifyingOtp.value) return;

  // Ensure batchUUID is available
  if (!batchUUID.value) {
    const currentState = history.state;
    if (currentState && currentState.batchUUID) {
      batchUUID.value = currentState.batchUUID;
      paymentMethod.value = currentState.paymentMethod;
      bankId.value = currentState.bankId;
    } else {
      store.dispatch("showToast", {
        type: "error",
        message: "Cannot verify OTP. Session data missing.",
      });
      isVerifyingOtp.value = false;
      return;
    }
  }

  isVerifyingOtp.value = true;
  try {
    const formData = new FormData();
    formData.append("otp", combinedOtp.value);
    formData.append("batch_id", batchUUID.value);
    // The verifyOtp API might need to return payment details for the next page
    const response = await verifyOtp(formData);
    if (response.status === "success") {
      // Check payment method and route accordingly
      if (paymentMethod.value === "balance") {
        // For balance payment, go directly to PaymentSuccess page
        console.log("edo", response.data);
        router.push({
          name: "PaymentSuccess",
          state: {
            batchId: batchUUID.value,
            paymentMethod: paymentMethod.value,
            totalAmount: response.data?.payment?.amount || 0,
            transactionId: response.data?.payment?.transaction_id || null,
            paymentDate:
              response.data?.payment_date || new Date().toISOString(),
          },
        });
      } else if (paymentMethod.value === "virtual_account") {
        // For virtual account payment, go to VirtualAccountDisplay page
        const paymentDataForNextPage = {
          batchId: response.data?.transaction_batch.uuid,
          virtualAccountNumber:
            response.data?.virtual_account.va_number || "N/A", // From API
          totalAmount: response.data?.payment.amount || 0, // From API
          accountName: "PT Anugrah Multi Remittance",
          expiresAt: response.data?.virtual_account.expires_at,
          bankName: response.data?.virtual_account.bank_name, // From API or determined from paymentMethod/bankId
          // bankLogo: 'path/to/logo.png', // This could be mapped in VirtualAccountDisplay based on bankName
        };

        router.push({
          name: "VirtualAccountDisplay",
          state: {
            paymentDetails: paymentDataForNextPage,
            expiresAt: response.data?.payment_expiry_time, // e.g., "2023-12-31T23:59:59Z"
          },
        });
      } else {
        // Fallback in case payment method is not recognized
        console.warn("Unknown payment method:", paymentMethod.value);
        store.dispatch("showToast", {
          type: "error",
          message: "Unknown payment method. Please contact support.",
        });
      }
    }
  } catch (error) {
    console.error("Error verifying OTP:", error);
    const errorMessage =
      error.response?.data?.message ||
      "Invalid OTP or an error occurred. Please try again.";
    store.dispatch("showToast", { type: "error", message: errorMessage });
    // Clear OTP fields on error
    otpDigits.value = ["", "", "", "", "", ""];
    if (otpInputs.value[0]) {
      otpInputs.value[0].focus();
    }
  }
  isVerifyingOtp.value = false;
};
</script>

<style scoped>
.otp-verification-container {
  display: flex;
  flex-direction: column; /* Ensure footer is below the card */
  align-items: center;
  justify-content: center; /* Center card vertically */
  min-height: 100vh;
  background-color: #e9f5e9; /* Light green background */
  padding: 20px;
}

.otp-card {
  background-color: #fff;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 450px;
  text-align: center;
}

.logo {
  max-width: 150px;
}

.otp-inputs {
  gap: 10px; /* Spacing between input fields */
}

.otp-input {
  width: 45px; /* Adjust width as needed */
  height: 50px; /* Adjust height as needed */
  font-size: 1.5rem;
  border: 1px solid #ced4da;
  border-radius: 0.375rem; /* Bootstrap's default form-control border-radius */
  text-align: center;
  caret-color: transparent; /* Hide caret for better aesthetics if desired */
}

.otp-input:focus {
  border-color: #007bff; /* Highlight focus */
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn-primary {
  background-color: #2dce89; /* Swiftrans green */
  border-color: #2dce89;
  padding: 12px;
  font-size: 1rem;
}

.btn-primary:hover {
  background-color: #26a671; /* Darker green on hover */
  border-color: #26a671;
}

.btn-primary:disabled {
  background-color: #a3e4c8; /* Lighter green when disabled */
  border-color: #a3e4c8;
}

.disabled-link {
  color: #6c757d; /* Muted color for disabled link */
  pointer-events: none;
  text-decoration: none;
}

.footer-text p,
.footer-text-bottom p {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 0;
}

.footer-text a,
.footer-text-bottom a {
  color: #007bff;
  text-decoration: none;
}

.footer-text a:hover,
.footer-text-bottom a:hover {
  text-decoration: underline;
}

.footer-text-bottom {
  position: fixed; /* Or absolute, depending on layout needs */
  bottom: 20px; /* Adjust as needed */
  left: 0;
  width: 100%;
  text-align: center;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .otp-card {
    padding: 20px;
  }
  .otp-input {
    width: 35px;
    height: 40px;
    font-size: 1.2rem;
  }
  .btn-primary {
    padding: 10px;
    font-size: 0.9rem;
  }
}
</style>
